/**
 * Run Scan Step - Execute first health check
 */

import { useState } from 'react';

export default function RunScan({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  const [scanState, setScanState] = useState<'Ready' | 'Running' | 'Complete'>(
    'Ready',
  );
  const [progress, setProgress] = useState(0);

  const handleRunScan = () => {
    setScanState('Running');
    setProgress(0);

    // Simulate scan progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setScanState('Complete');
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  return (
    <div className='bg-white shadow sm:rounded-lg'>
      <div className='px-4 py-5 sm:p-6'>
        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
          Run Your First Scan
        </h2>

        {scanState === 'Ready' && (
          <div className='space-y-4'>
            <p className='text-gray-600'>
              You're all set! Click below to run your first health check. All
              analysis runs locally in your browser, no page content leaves your
              device.
            </p>
            <div className='rounded-md bg-blue-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-blue-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3 flex-1'>
                  <p className='text-sm text-blue-700'>
                    Zero-trust processing: Your sensitive content stays private
                    and never leaves your browser.
                  </p>
                </div>
              </div>
            </div>
            <div className='mt-6'>
              <button
                onClick={handleRunScan}
                type='button'
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2ba6bb] hover:bg-[#228a9d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ba6bb]'
              >
                Run Scan
              </button>
            </div>
          </div>
        )}

        {scanState === 'Running' && (
          <div className='space-y-4'>
            <p className='text-gray-600'>Scanning in progress...</p>
            <div className='w-full bg-gray-200 rounded-full h-2.5'>
              <div
                className='bg-[#2ba6bb] h-2.5 rounded-full transition-all duration-300'
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className='text-sm text-gray-500'>{progress}% complete</p>
          </div>
        )}

        {scanState === 'Complete' && (
          <div className='space-y-4'>
            <div className='rounded-md bg-[#2ba6bb]/10 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-[#2ba6bb]'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-[#228a9d]'>
                    Scan complete!
                  </p>
                  <p className='mt-1 text-sm text-[#228a9d]'>
                    Your health check results are ready. You can now access the
                    full dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='mt-6 flex items-center justify-between'>
          <button
            onClick={onBack}
            disabled={scanState === 'Running'}
            type='button'
            className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
              scanState === 'Running'
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'text-gray-700 bg-white hover:bg-gray-50'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ba6bb]`}
          >
            Back
          </button>
          {scanState === 'Complete' && (
            <button
              onClick={onNext}
              type='button'
              className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2ba6bb] hover:bg-[#228a9d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ba6bb]'
            >
              Finish Setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
